import { GridModel, GridModelError } from './model'
import type {
  DecodedImageAssetRecord,
  DecodedImageBarrierRecord,
  DecodedImagePlacement,
  DecodedImageSceneRecord,
  DecodedScreenFrame,
  ImageAssetReservation,
} from './protocol'
import { GRID_IMAGE_MAX_PLACEMENTS } from './protocol'

export type ImagePresentationRecoveryReason = 'decode' | 'gap' | 'generation' | 'asset' | 'context'

export interface RetainedImageAssets {
  readonly activeEpoch: number
  hasAsset(assetId: number): boolean
  accept(
    record: DecodedImageAssetRecord,
    reservation?: ImageAssetReservation | null,
  ): Promise<'stored' | 'duplicate'>
}

export interface ImagePresentationCommit {
  readonly grid: GridModel
  readonly scene: DecodedImageSceneRecord
  readonly presentationGeneration: number
}

export class ImagePresentationError extends Error {
  constructor(readonly reason: ImagePresentationRecoveryReason, message: string) {
    super(message)
    this.name = 'ImagePresentationError'
  }
}

function fail(reason: ImagePresentationRecoveryReason, message: string): never {
  throw new ImagePresentationError(reason, `invalid image presentation: ${message}`)
}

function uint32(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 0xffffffff
}

function nonzeroUint32(value: unknown): value is number {
  return uint32(value) && value !== 0
}

function uint16(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 0xffff
}

function copyPlacement(placement: DecodedImagePlacement): DecodedImagePlacement {
  return Object.freeze({
    assetId: placement.assetId,
    x: placement.x,
    y: placement.y,
    width: placement.width,
    height: placement.height,
    fit: placement.fit,
    filter: placement.filter,
  })
}

function copyScene(scene: DecodedImageSceneRecord): DecodedImageSceneRecord {
  return Object.freeze({
    assetEpoch: scene.assetEpoch,
    sceneGeneration: scene.sceneGeneration,
    gridSequence: scene.gridSequence,
    configGeneration: scene.configGeneration,
    resizeGeneration: scene.resizeGeneration,
    cols: scene.cols,
    rows: scene.rows,
    placements: Object.freeze(scene.placements.map(copyPlacement)),
  })
}

export class ImagePresentationCoordinator {
  readonly #assets: RetainedImageAssets
  #activeEpoch = 0
  #committedScene?: DecodedImageSceneRecord
  #stagedScene?: DecodedImageSceneRecord
  #lastPresentationGeneration = 0
  #pendingGrid?: GridModel

  constructor(assets: RetainedImageAssets) {
    this.#assets = assets
  }

  get activeEpoch(): number { return this.#activeEpoch }
  get committedScene(): DecodedImageSceneRecord | undefined { return this.#committedScene }
  get committedSceneGeneration(): number { return this.#committedScene?.sceneGeneration ?? 0 }
  get committedPlacementCount(): number { return this.#committedScene?.placements.length ?? 0 }
  get lastPresentationGeneration(): number { return this.#lastPresentationGeneration }
  get pendingGridSequence(): number { return this.#pendingGrid?.sequence ?? 0 }

  activate(assetEpoch: number): void {
    if (!nonzeroUint32(assetEpoch) || this.#assets.activeEpoch !== assetEpoch) {
      fail('generation', 'asset epoch is not active in the retained store')
    }
    this.#activeEpoch = assetEpoch
    this.#committedScene = undefined
    this.#stagedScene = undefined
    this.#lastPresentationGeneration = 0
    this.#pendingGrid = undefined
  }

  async acceptAsset(
    record: DecodedImageAssetRecord,
    reservation?: ImageAssetReservation | null,
  ): Promise<'stored' | 'duplicate'> {
    this.#requireActive()
    return await this.#assets.accept(record, reservation)
  }

  stageGrid(frame: DecodedScreenFrame, committed: GridModel): void {
    this.#requireActive()
    const base = this.#pendingGrid ?? committed
    if (!nonzeroUint32(frame.sequence)) fail('generation', 'staged grid sequence is invalid')
    if (frame.kind === 'patch') {
      if (frame.previousSequence !== base.sequence) {
        fail('gap', 'staged grid patch does not follow the available grid')
      }
      if (frame.configGeneration !== base.configGeneration ||
          frame.resizeGeneration !== base.resizeGeneration || frame.cols !== base.cols ||
          frame.rows !== base.rows) {
        fail('generation', 'staged grid patch metadata does not match the available grid')
      }
    } else if (frame.sequence <= base.sequence) {
      fail('generation', 'staged full grid is not newer than the available grid')
    }
    const staged = base.clone()
    try {
      if (frame.kind === 'full') staged.applyFull(frame)
      else staged.applyPatch(frame)
    } catch (error) {
      if (error instanceof GridModelError) fail(error.reason, error.message)
      fail('context', 'staged grid application failed')
    }
    this.#pendingGrid = staged
  }

  stageScene(source: DecodedImageSceneRecord, committed: GridModel): void {
    this.#requireActive()
    const grid = source.gridSequence === this.#pendingGrid?.sequence
      ? this.#pendingGrid
      : source.gridSequence === committed.sequence ? committed : undefined
    if (grid === undefined || !nonzeroUint32(source.sceneGeneration) ||
        source.assetEpoch !== this.#activeEpoch || source.assetEpoch !== this.#assets.activeEpoch ||
        source.configGeneration !== grid.configGeneration ||
        source.resizeGeneration !== grid.resizeGeneration || source.cols !== grid.cols ||
        source.rows !== grid.rows || !Array.isArray(source.placements) ||
        source.placements.length > GRID_IMAGE_MAX_PLACEMENTS) {
      fail('generation', 'scene does not exactly match an available grid and active epoch')
    }
    if (this.#committedScene !== undefined &&
        source.sceneGeneration <= this.#committedScene.sceneGeneration) {
      fail('generation', 'scene generation is not newer than the committed scene')
    }
    if (this.#stagedScene !== undefined &&
        source.sceneGeneration <= this.#stagedScene.sceneGeneration) {
      fail('generation', 'scene generation is not newer than the staged scene')
    }
    for (const placement of source.placements) this.#validatePlacement(placement, grid)
    this.#stagedScene = copyScene(source)
  }

  commit(barrier: DecodedImageBarrierRecord, committed: GridModel): ImagePresentationCommit {
    this.#requireActive()
    if (barrier.assetEpoch !== this.#activeEpoch || barrier.assetEpoch !== this.#assets.activeEpoch ||
        !nonzeroUint32(barrier.presentationGeneration) ||
        (this.#lastPresentationGeneration !== 0 &&
         barrier.presentationGeneration <= this.#lastPresentationGeneration)) {
      fail('generation', 'barrier epoch or presentation generation is not strictly newer')
    }

    let grid: GridModel
    if (this.#pendingGrid !== undefined) {
      if (barrier.gridSequence !== this.#pendingGrid.sequence) {
        fail('generation', 'barrier does not name the staged grid')
      }
      grid = this.#pendingGrid
    } else {
      if (barrier.gridSequence !== committed.sequence) {
        fail('generation', 'barrier does not name the committed grid')
      }
      grid = committed
    }

    const stagedScene = this.#stagedScene
    let scene: DecodedImageSceneRecord | undefined
    if (stagedScene !== undefined) {
      if (barrier.sceneGeneration !== stagedScene.sceneGeneration) {
        fail('generation', 'barrier does not name the staged scene')
      }
      scene = stagedScene
    } else {
      if (barrier.sceneGeneration !== this.#committedScene?.sceneGeneration) {
        fail('generation', 'barrier does not name the committed scene')
      }
      scene = this.#committedScene
    }

    if (scene === undefined || barrier.configGeneration !== grid.configGeneration ||
        barrier.resizeGeneration !== grid.resizeGeneration ||
        scene.configGeneration !== grid.configGeneration ||
        scene.resizeGeneration !== grid.resizeGeneration || scene.cols !== grid.cols ||
        scene.rows !== grid.rows) {
      fail('generation', 'barrier halves do not share exact configuration and dimensions')
    }

    this.#pendingGrid = undefined
    this.#stagedScene = undefined
    this.#committedScene = scene
    this.#lastPresentationGeneration = barrier.presentationGeneration
    return Object.freeze({
      grid,
      scene,
      presentationGeneration: barrier.presentationGeneration,
    })
  }

  clearForRecovery(): void {
    this.#pendingGrid = undefined
    this.#stagedScene = undefined
    this.#committedScene = undefined
  }

  reset(): void {
    this.#activeEpoch = 0
    this.#committedScene = undefined
    this.#stagedScene = undefined
    this.#lastPresentationGeneration = 0
    this.#pendingGrid = undefined
  }

  #requireActive(): void {
    if (this.#activeEpoch === 0 || this.#assets.activeEpoch !== this.#activeEpoch) {
      fail('generation', 'image feature epoch is not active')
    }
  }

  #validatePlacement(placement: DecodedImagePlacement, grid: GridModel): void {
    if (placement === null || typeof placement !== 'object' ||
        !nonzeroUint32(placement.assetId) || !uint16(placement.x) ||
        !uint16(placement.y) || !uint16(placement.width) || placement.width === 0 ||
        !uint16(placement.height) || placement.height === 0 ||
        placement.x >= grid.cols || placement.y >= grid.rows ||
        (placement.fit !== 'contain' && placement.fit !== 'stretch') ||
        (placement.filter !== 'nearest' && placement.filter !== 'linear')) {
      fail('generation', 'scene placement is invalid for the referenced grid')
    }
    if (!this.#assets.hasAsset(placement.assetId)) {
      fail('asset', 'scene references an asset that is not retained in the active epoch')
    }
  }
}
